import { useCallback, useMemo } from 'react'
import * as React from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { dateIsAfter, dateDifference } from 'lib/datetime/dateUtils'
import DateInput from 'components/Common/Form/Input/DateInput'

interface Props extends Partial<React.ComponentProps<typeof DateInput>> {
  format?: 'DD/MM/YYYY' | 'MM/DD/YYYY'
  travellerType?: string
  refDate?: Date
}

const MAX_SENSIBLE_AGE = 150

const isFutureDate = (date: Date) => { return dateIsAfter(date, new Date()) }
const yearDifferenceFromNow = (date: Date | undefined, refDate: Date) => {
  const difference = dateDifference(refDate, date)
  return Math.round(difference.days / 365)
}
const monthDifferenceFromNow = (date: Date | undefined, refDate: Date) => {
  const difference = dateDifference(refDate, date)
  return Math.round(difference.days / 30)
}

function DateOfBirthInput(props: Props) {
  const { min, max, format = 'DD/MM/YYYY', travellerType = 'adult', ...rest } = props
  const intl = useIntl()

  const validationBounds = useMemo(() => {
    const refDate = props.refDate || new Date()
    return {
      upperAgeBound: yearDifferenceFromNow(min, refDate),
      lowerAgeBound: yearDifferenceFromNow(max, refDate),
      upperMonthsBound: monthDifferenceFromNow(min, refDate),
      lowerMonthsBound: monthDifferenceFromNow(max, refDate),
    }
  }, [max, min, props.refDate])

  const getInvalidMessage = useCallback((e: HTMLInputElement): string | undefined => {
    // DateInput.setCustomValidity uses plain English signals (not localized) so this comparison is safe
    if (e.validationMessage === 'Invalid date') { return e.validationMessage }
    if (e.validity.patternMismatch) { return undefined }

    const inputDateValue = moment(e.value, format).toDate()
    if (isFutureDate(inputDateValue)) {
      return intl.formatMessage({
        id: 'gLzjaEUhFtG9geYszbBr',
        defaultMessage: 'Cannot be in the future',
        description: 'Validation error when date of birth is in the future',
      })
    }

    const { upperAgeBound, lowerAgeBound, upperMonthsBound, lowerMonthsBound } = validationBounds

    let message: string | undefined
    if (e.validity.customError && e.validationMessage) {
      // Error message for traveller that is an adult or a child (with adult pricing)
      if (travellerType === 'adult' || (travellerType === 'child' && upperAgeBound === MAX_SENSIBLE_AGE)) {
        // DateInput.setCustomValidity uses plain English signals (not localized) so this comparison is safe
        const isTooYoung = e.validationMessage.startsWith('Must be before')
        message = isTooYoung
          ? intl.formatMessage(
              {
                id: '5cI3_r1V_talNdnlaZxb',
                defaultMessage: 'Must be over {age} years old',
                description: 'Validation error when traveller is younger than the minimum required age',
              },
              { age: lowerAgeBound },
            )
          : intl.formatMessage({
              id: 'ue_6skJ2-2tFz0HhqsVr',
              defaultMessage: 'Invalid Date',
              description: 'Generic invalid date validation error in the date selection or calendar picker',
            })
      } else if (lowerMonthsBound > 0 && lowerMonthsBound < 12 && upperAgeBound) {
        message = intl.formatMessage(
          {
            id: 'A-2eUUT9eXiXoYH3bYsL',
            defaultMessage: 'Must be between {months, plural, one {# month} other {# months}}-{years} years old',
            description: 'Age validation when minimum age is in months and maximum is in years',
          },
          { months: lowerMonthsBound, years: upperAgeBound },
        )
      } else if (lowerAgeBound || upperAgeBound) {
        message = intl.formatMessage(
          {
            id: 'NZZkgTq8qxciNi2ePtTR',
            defaultMessage: 'Must be between {lower}-{upper} years old',
            description: 'Age validation for travellers within a year range in the input section',
          },
          { lower: lowerAgeBound, upper: upperAgeBound },
        )
      } else {
        message = intl.formatMessage(
          {
            id: 'TZ-pVZ0ceG1mmcBFe3mo',
            defaultMessage: 'Must be between {lower}-{upper} months old',
            description: 'Age validation for infants within a month range in the input section',
          },
          { lower: lowerMonthsBound, upper: upperMonthsBound },
        )
      }
    }
    return message
  }, [format, intl, travellerType, validationBounds])

  return (
    <DateInput
      {...rest}
      min={min}
      max={max}
      displayFormat={format}
      getInvalidMessage={getInvalidMessage}
    />
  )
}

export default DateOfBirthInput
